import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface LogoProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  to?: string;
  variant?: "full" | "mark" | "wordmark";
  /** Force a monochrome mark (uses currentColor). Useful on colored backgrounds. */
  mono?: boolean;
}

const sizeMap = {
  sm: { mark: 22, text: "text-base", gap: "gap-1.5" },
  md: { mark: 28, text: "text-xl", gap: "gap-2" },
  lg: { mark: 36, text: "text-2xl", gap: "gap-2.5" },
  xl: { mark: 48, text: "text-4xl", gap: "gap-3" },
} as const;

/**
 * Brand mark: a rounded-square "signal bubble" — two stacked speech shapes
 * forming an abstract SF monogram. Anonymous feedback → layered voices.
 */
export const LogoMark = ({
  size = 28,
  mono = false,
  className,
}: {
  size?: number;
  mono?: boolean;
  className?: string;
}) => {
  const gid = `sf-mark-${size}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("shrink-0", className)}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#3A3733" />
          <stop offset="55%" stopColor="#26231F" />
          <stop offset="100%" stopColor="#161412" />
        </linearGradient>
      </defs>
      {/* Tile */}
      <rect
        x="0"
        y="0"
        width="40"
        height="40"
        rx="11"
        fill={mono ? "currentColor" : `url(#${gid})`}
      />
      {/* Back speech shape (offset) */}
      <path
        d="M11 12.5c0-1.38 1.12-2.5 2.5-2.5h10.75c1.38 0 2.5 1.12 2.5 2.5v6.75c0 1.38-1.12 2.5-2.5 2.5H18.5l-2.75 2.6a.6.6 0 0 1-1.01-.44v-2.16h-1.24C11.12 21.75 10 20.63 10 19.25V12.5Z"
        fill="#FFFFFF"
        fillOpacity="0.35"
      />
      {/* Front speech shape */}
      <path
        d="M15 17c0-1.66 1.34-3 3-3h10a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-4.25l-3.28 3.1a.7.7 0 0 1-1.18-.51V26H18a3 3 0 0 1-3-3v-6Z"
        fill="#FFFFFF"
      />
      {/* Anonymity dot / signal */}
      <circle cx="23" cy="20" r="1.4" fill="#4F46E5" />
    </svg>
  );
};

const Logo = ({ className, size = "md", to, variant = "full", mono = false }: LogoProps) => {
  const cfg = sizeMap[size];

  const content = (
    <span className={cn("inline-flex items-center", cfg.gap)}>
      {variant !== "wordmark" && <LogoMark size={cfg.mark} mono={mono} />}
      {variant !== "mark" && (
        <span
          className={cn(
            "font-sans font-black tracking-tight leading-none text-foreground",
            cfg.text,
          )}
        >
          sendfeedback<span className="text-primary font-bold">.ai</span>
        </span>
      )}
    </span>
  );

  if (to) {
    return (
      <Link
        to={to}
        aria-label="sendfeedback.ai — home"
        className={cn("inline-flex items-center transition-opacity duration-200 hover:opacity-80", className)}
      >
        {content}
      </Link>
    );
  }
  return <div className={cn("inline-flex items-center", className)}>{content}</div>;
};

export default Logo;
